import React, { useState } from "react";

interface Props {
  /** Name of the external data source, e.g. "NetSuite", "Shopify". Omit for generic AI Chatbot node. */
  dataSourceName?: string;
  /** Route to add a data source config, e.g. "/integrations/netsuite" */
  dataSourcePath?: string;
  /** Whether an AI config is selected/available */
  hasAiConfig: boolean;
  /** Whether at least one MCP tool server is configured */
  hasMcpServers: boolean;
  /** Whether a data source config is selected (only relevant when dataSourceName is set) */
  hasDataSourceConfig?: boolean;
}

interface Step {
  num: string;
  title: string;
  body: string;
  status: "ok" | "missing" | "optional";
}

export default function AISetupGuide({
  dataSourceName,
  dataSourcePath,
  hasAiConfig,
  hasMcpServers,
  hasDataSourceConfig
}: Props) {
  const [open, setOpen] = useState(false);

  const steps: Step[] = [
    {
      num: "1",
      title: "AI Config — the brain",
      body:
        'Choose which AI model handles the conversation. The system prompt (in Bots → AI Configs) is where you write what the AI can do, how it should behave, and what tools to use. This is the most important thing to get right.',
      status: hasAiConfig ? "ok" : "missing"
    },
    ...(dataSourceName
      ? [
          {
            num: "2",
            title: `${dataSourceName} Config — credentials`,
            body: `Connects the AI to your ${dataSourceName} account. Add a config under Integrations → ${dataSourceName} first, then select it here.`,
            status: (hasDataSourceConfig ? "ok" : "missing") as "ok" | "missing" | "optional"
          }
        ]
      : []),
    {
      num: dataSourceName ? "3" : "2",
      title: "MCP Tool Servers — what the AI can actually do",
      body:
        `These are the API tools the AI calls at runtime — things like "search_orders", "lookup_customer", "find_case". Add MCP servers under Integrations → MCP Tool Servers, then enable them here. Without tools, the AI can only chat; it can't look anything up.`,
      status: hasMcpServers ? "ok" : "optional"
    },
    {
      num: dataSourceName ? "4" : "3",
      title: "Specialist Agents — optional orchestration",
      body:
        "If a single AI handles too many topics, split them into specialists. The primary AI automatically delegates to a specialist when the conversation matches its scope, then resumes when the specialist is done. Specialists don't need their own workflow nodes — just AI Configs.",
      status: "optional"
    }
  ];

  const channelNotes = [
    { channel: "Web", note: "Full formatting, clickable links, rich media — use freely." },
    {
      channel: "SMS",
      note:
        "No clickable URLs. Use numbered lists instead of bullets. Offer to send a text link if the customer needs a URL. Keep messages short."
    },
    {
      channel: "IVR (Voice)",
      note:
        "Verbal only — no formatting, no URLs. Offer to send details via SMS or email. Spell out numbers and options clearly."
    },
    { channel: "WhatsApp", note: "Light formatting OK. Avoid complex tables or embedded images." },
    { channel: "Email", note: "Rich HTML formatting, full links, attachments." }
  ];

  const statusDot = (s: Step["status"]) =>
    s === "ok" ? (
      <span className="text-green-600 font-bold text-sm mt-0.5 shrink-0" aria-label="Done">
        ✓
      </span>
    ) : s === "missing" ? (
      <span className="text-amber-600 font-bold text-sm mt-0.5 shrink-0" aria-label="Required">
        !
      </span>
    ) : (
      <span className="text-slate-400 font-bold text-sm mt-0.5 shrink-0" aria-label="Optional">
        ○
      </span>
    );

  return (
    <div className="mb-3 border border-slate-200 rounded bg-slate-50 text-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-slate-700 hover:text-slate-900 transition-colors"
        aria-expanded={open}
      >
        <span className="font-medium text-xs">
          How{dataSourceName ? ` ${dataSourceName} AI` : " AI Chatbot"} nodes work
        </span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="px-3 pb-3 flex flex-col gap-4">
          {/* Setup steps */}
          <div className="flex flex-col gap-3">
            {steps.map((step) => (
              <div key={step.num} className="flex gap-2.5">
                <div className="flex flex-col items-center gap-1">
                  <span className="w-5 h-5 rounded-full bg-slate-700 text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {step.num}
                  </span>
                  {statusDot(step.status)}
                </div>
                <div>
                  <p className="font-semibold text-slate-800 text-xs">{step.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{step.body}</p>
                  {step.status === "missing" && step.num === "1" && (
                    <a
                      href="/bots"
                      className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                    >
                      Go to AI Configs →
                    </a>
                  )}
                  {step.status === "missing" && dataSourcePath && step.num === "2" && (
                    <a
                      href={dataSourcePath}
                      className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                    >
                      Go to {dataSourceName} Integrations →
                    </a>
                  )}
                  {step.status === "optional" &&
                    (step.num === "2" || step.num === "3") &&
                    !dataSourceName && (
                      <a
                        href="/integrations/mcp"
                        className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                      >
                        Add MCP Tool Servers →
                      </a>
                    )}
                </div>
              </div>
            ))}
          </div>

          {/* Orchestration / jump-back explanation */}
          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs font-semibold text-slate-700 mb-1">How delegation works</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              The primary AI calls a <code className="bg-slate-200 px-1 rounded font-mono">delegate_to_specialist</code> tool
              automatically when the conversation matches a specialist's scope. The specialist runs
              with its own prompt and tools, then calls{" "}
              <code className="bg-slate-200 px-1 rounded font-mono">return_to_previous</code> when
              done — control returns to the primary AI. Specialists can be nested up to 5 levels
              deep.
            </p>
          </div>

          {/* Channel behavior */}
          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs font-semibold text-slate-700 mb-1.5">
              Channel behavior — built-in
            </p>
            <p className="text-xs text-slate-500 mb-2 leading-relaxed">
              The system automatically adapts AI responses per channel. You can reinforce these in
              your system prompt for consistency:
            </p>
            <div className="flex flex-col gap-1.5">
              {channelNotes.map((c) => (
                <div key={c.channel} className="flex gap-2">
                  <span className="text-xs font-medium text-slate-700 w-20 shrink-0">{c.channel}</span>
                  <span className="text-xs text-slate-500">{c.note}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
